// @ts-strict-ignore
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  HostListener,
  Inject,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { filter, timer } from 'rxjs';
import { injectUntilDestroyed } from '../../../../functions/inject-functions/inject-until-destroyed';
import { UI_STRINGS } from '../../../../functions/language/uistrings';
import { AppCookieService } from '../../../../services/cookie-service/app-cookie.service';
import { SharedModule } from '../../shared.module';
import { InputCheckboxComponent } from '../../ui-kit/input-checkbox/input-checkbox.component';
import { GlobalIcon } from '../icon/icon.constants';

@Component({
  selector: 'app-cookie-dialogue',
  templateUrl: './cookie-dialogue.component.html',
  styleUrls: ['./cookie-dialogue.component.scss'],
  imports: [SharedModule, InputCheckboxComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class CookieDialogueComponent {
  @HostBinding('style.display') get isDialogueOpenDisplay() {
    return this._isDialogOpen ? 'unset' : 'none';
  }
  @HostListener('window:keydown.esc', ['$event'])
  onEsc() {
    if (this._isDialogOpen) {
      this._toggleDialogue(false);
    }
  }

  cookiePerformanceControl = new FormControl<boolean>(false);
  cookiePreferencesControl = new FormControl<boolean>(false);
  cookieRequiredControl = new FormControl<boolean>(true);
  isShowingOptions: boolean = false;

  private _isConsentDeniedThisSession: boolean;
  private _isDialogOpen: boolean;
  private _openedViaClick: boolean = false;

  readonly DIALOGUE_STRINGS = UI_STRINGS.COOKIE_DIALOG;
  readonly GLOBAL_STRINGS = UI_STRINGS.GLOBAL;
  readonly X_ICON = GlobalIcon.X;

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private _cdr: ChangeDetectorRef,
    private _cookieService: AppCookieService,
    private _router: Router
  ) {
    const cookie = this._cookieService.getConsentCookie() ?? this._cookieService.getDefaultCookie();
    this.cookiePerformanceControl.setValue(cookie.performance);
    this.cookiePreferencesControl.setValue(cookie.preferences);

    this._router.events
      .pipe(
        this._untilDestroyed(),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        if (!this._cookieService.hasConsented() && !this._isConsentDeniedThisSession) {
          this._toggleDialogue(true);
        }
      });
    this._cookieService.openDialogRequest.pipe(this._untilDestroyed()).subscribe((viaClick) => {
      this._openedViaClick = viaClick;
      this._toggleDialogue(true);
    });
  }

  accept(all?: true) {
    this._cookieService.setConsent(
      all ?? this.cookiePreferencesControl.value,
      all ?? this.cookiePerformanceControl.value
    );
    if (all) {
      this.cookiePreferencesControl.setValue(true);
      this.cookiePerformanceControl.setValue(true);
    }
    this._cdr.markForCheck();
    this._toggleDialogue(false);
  }

  closeDialogue() {
    this._toggleDialogue(false);
  }

  deny() {
    this.cookiePreferencesControl.setValue(false);
    this.cookiePerformanceControl.setValue(false);
    this._isConsentDeniedThisSession = true;
    this._cookieService.clearCookies();
    this._cdr.markForCheck();
    this._toggleDialogue(false);
  }

  toggleOptions() {
    this.isShowingOptions = !this.isShowingOptions;
    if (this.isShowingOptions) {
      this._cdr.markForCheck();
      this._document.getElementById('cookie-dialogue-options')?.focus();
    }
  }

  private _toggleDialogue(open: boolean) {
    this.isShowingOptions = false;
    if (open) {
      timer(0).subscribe(() => this._document.getElementById('cookie-dialogue-heading')?.focus());
    } else {
      this._cookieService.closeDialog();
      if (this._openedViaClick) {
        timer(0).subscribe(() => this._document.getElementById('cookie-settings-button')?.focus());
      }
    }
    this._isDialogOpen = open;
    this._cdr.markForCheck();
  }

  private _untilDestroyed = injectUntilDestroyed();
}
